import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, Check, AlertCircle, Receipt, Lock } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmt = (a) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);

export default function PublicInvoicePay() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [pollMessage, setPollMessage] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/public/invoices/${token}`);
      setInvoice(data);
    } catch (e) {
      setError(
        e?.response?.status === 404
          ? 'This invoice link is no longer valid.'
          : 'Could not load invoice.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Return-from-Stripe polling
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');
    if (canceled) {
      setPollMessage(null);
      const np = new URLSearchParams(searchParams);
      np.delete('canceled');
      setSearchParams(np, { replace: true });
      return;
    }
    if (!sessionId) return;

    let attempts = 0;
    let cancelled = false;
    setPollMessage('Confirming payment...');
    const poll = async () => {
      if (cancelled) return;
      if (attempts >= 10) {
        setPollMessage(null);
        return;
      }
      attempts += 1;
      try {
        const { data } = await axios.get(
          `${API_BASE}/api/public/invoices/checkout-status/${sessionId}`,
        );
        if (data.payment_status === 'paid') {
          setPollMessage(null);
          const np = new URLSearchParams(searchParams);
          np.delete('session_id');
          setSearchParams(np, { replace: true });
          load();
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setPollMessage(null);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('session_id'), searchParams.get('canceled')]);

  const handlePay = async () => {
    setCheckingOut(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/api/public/invoices/${token}/checkout`,
        { origin_url: window.location.origin },
      );
      window.location.href = data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not start checkout.');
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <FullPage>
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </FullPage>
    );
  }

  if (error) {
    return (
      <FullPage>
        <AlertCircle className="h-12 w-12 text-rose-500 mb-3" />
        <p className="text-slate-700">{error}</p>
      </FullPage>
    );
  }

  const isPaid = invoice.status === 'paid';

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 py-10 px-4" data-testid="public-invoice-page">
      <div className="max-w-2xl mx-auto">
        {/* Brand header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg overflow-hidden bg-white border flex items-center justify-center">
            <img src="/brand/logo.png" alt="My Key Boss" className="w-full h-full object-contain" />
          </div>
          <div>
            <p className="text-slate-900 font-bold">{invoice.company?.name || 'My Key Boss'}</p>
            <p className="text-xs text-slate-500">{invoice.company?.email}</p>
          </div>
        </div>

        {/* Invoice card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200">
          {/* Top banner */}
          <div
            className={`px-6 py-5 ${isPaid ? 'bg-emerald-500' : 'bg-gradient-to-r from-sky-500 to-violet-600'} text-white`}
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-xs uppercase tracking-wider opacity-80">Invoice</p>
                <p className="text-xl font-bold" data-testid="public-inv-number">
                  {invoice.invoice_number}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wider opacity-80">Amount Due</p>
                <p className="text-3xl font-bold" data-testid="public-inv-total">
                  {fmt(invoice.total)}
                </p>
              </div>
            </div>
          </div>

          {/* Status */}
          {isPaid ? (
            <div
              className="px-6 py-4 bg-emerald-50 border-b border-emerald-200 flex items-center gap-2 text-emerald-700"
              data-testid="public-inv-paid-banner"
            >
              <Check className="h-5 w-5" />
              <span className="font-semibold">
                Paid {invoice.paid_at ? `on ${invoice.paid_at.slice(0, 10)}` : ''}
              </span>
            </div>
          ) : (
            invoice.due_at && (
              <div className="px-6 py-3 bg-amber-50 border-b border-amber-200 text-sm text-amber-700">
                Due {invoice.due_at.slice(0, 10)}
              </div>
            )
          )}

          {/* Body */}
          <div className="px-6 py-5 space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Bill To</p>
              <p className="font-medium text-slate-900">{invoice.customer?.name}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Service</p>
              <p className="font-medium text-slate-900">{invoice.title}</p>
              {invoice.description && (
                <p className="text-sm text-slate-600">{invoice.description}</p>
              )}
            </div>

            {/* Line items */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-700 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2">Description</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-right px-3 py-2">Price</th>
                    <th className="text-right px-3 py-2">Amount</th>
                  </tr>
                </thead>
                <tbody className="text-slate-800">
                  {(invoice.line_items || []).map((li, i) => {
                    const qty = Number(li.quantity || 1);
                    const price = Number(li.unit_price || li.price || 0);
                    return (
                      <tr key={i} className="border-t border-slate-200">
                        <td className="px-3 py-2">{li.description || li.name}</td>
                        <td className="px-3 py-2 text-right">{qty}</td>
                        <td className="px-3 py-2 text-right">{fmt(price)}</td>
                        <td className="px-3 py-2 text-right font-semibold">{fmt(qty * price)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal</span>
                <span>{fmt(invoice.subtotal)}</span>
              </div>
              {invoice.tax > 0 && (
                <div className="flex justify-between text-slate-600">
                  <span>Tax ({invoice.tax_rate_percent}%)</span>
                  <span>{fmt(invoice.tax)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold text-slate-900 pt-2 border-t">
                <span>Total</span>
                <span>{fmt(invoice.total)}</span>
              </div>
            </div>

            {invoice.notes && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                {invoice.notes}
              </div>
            )}
          </div>

          {/* Pay action */}
          {!isPaid && (
            <div className="px-6 pb-6">
              {pollMessage && (
                <div className="mb-3 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {pollMessage}
                </div>
              )}
              <button
                onClick={handlePay}
                disabled={checkingOut}
                data-testid="public-pay-now-button"
                className="w-full py-4 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-lg font-bold transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {checkingOut ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Redirecting to Stripe...
                  </>
                ) : (
                  <>
                    <Lock className="h-5 w-5" /> Pay {fmt(invoice.total)} Securely
                  </>
                )}
              </button>
              <p className="text-xs text-center text-slate-400 mt-3">
                Powered by Stripe. Your card details never touch our servers.
              </p>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6 flex items-center justify-center gap-1">
          <Receipt className="h-3 w-3" /> My Key Boss · Locksmith Business Management
        </p>
      </div>
    </div>
  );
}

const FullPage = ({ children }) => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center">
    {children}
  </div>
);
